import "./MovePagesModal.scss";

import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleModal from "components/Modals/KaboodleModal";
import { validateMoveTarget } from "../../utils/movePagesValidation";

import type { DocumentGroup } from "services/document-group-api";

type MovePagesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (targetPage: number) => void;
  onSelectPosition?: () => void;
  selectedPages: Set<number>;
  totalPages: number;
  documentGroups?: DocumentGroup[];
  isMoving?: boolean;
  preselectedTarget?: number | null;
};

const MovePagesModal = ({
  isOpen,
  onClose,
  onConfirm,
  onSelectPosition,
  selectedPages,
  totalPages,
  documentGroups = [],
  isMoving = false,
  preselectedTarget = null,
}: MovePagesModalProps) => {
  const [targetPage, setTargetPage] = useState<string>("");
  const [error, setError] = useState<string>("");

  const sortedPages = Array.from(selectedPages).sort((a, b) => a - b);
  const pageCount = selectedPages.size;

  // set preselected target when modal opens
  useEffect(() => {
    if (isOpen && preselectedTarget !== null) {
      setTargetPage(String(preselectedTarget));
    }
  }, [isOpen, preselectedTarget]);

  // validate target position whenever it changes (including preselected)
  useEffect(() => {
    if (!targetPage || !isOpen) {
      setError("");
      return;
    }

    const validationError = validateMoveTarget({
      targetPageInput: targetPage,
      totalPages,
      selectedPages,
      documentGroups,
    });
    setError(validationError ?? "");
  }, [targetPage, isOpen, totalPages, selectedPages, documentGroups]);

  // format page ranges for display (handles non-contiguous selections)
  const formatPageRanges = () => {
    if (pageCount === 0) return "";
    if (pageCount === 1) return `Page ${sortedPages[0]}`;

    // group consecutive pages into ranges
    const ranges: string[] = [];
    let rangeStart = sortedPages[0];
    let rangeEnd = sortedPages[0];

    for (let i = 1; i < sortedPages.length; i++) {
      if (sortedPages[i] === rangeEnd + 1) {
        rangeEnd = sortedPages[i];
      } else {
        // add the completed range
        ranges.push(
          rangeStart === rangeEnd
            ? `${rangeStart}`
            : `${rangeStart}-${rangeEnd}`,
        );
        rangeStart = sortedPages[i];
        rangeEnd = sortedPages[i];
      }
    }

    // add the final range
    ranges.push(
      rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`,
    );

    return `Page${pageCount > 1 ? "s" : ""} ${ranges.join(", ")}`;
  };

  const pageRange = formatPageRanges();

  // validate document groups when modal opens or selection changes
  useEffect(() => {
    if (!isOpen || selectedPages.size === 0) {
      setError("");
      return;
    }

    // check if any pages are in document groups
    if (documentGroups.length === 0) {
      setError("");
      return;
    }

    // find which document groups contain selected pages
    const affectedGroups: DocumentGroup[] = [];
    const pagesInGroups = new Set<number>();

    for (const group of documentGroups) {
      const groupPages = new Set<number>();
      for (let page = group.start_pg; page <= group.end_pg; page++) {
        groupPages.add(page);
      }

      // check if any selected pages are in this group
      let hasSelectedPages = false;
      let allGroupPagesSelected = true;

      for (const page of groupPages) {
        if (selectedPages.has(page)) {
          hasSelectedPages = true;
          pagesInGroups.add(page);
        } else {
          allGroupPagesSelected = false;
        }
      }

      // if some but not all pages of a group are selected, that's an error
      if (hasSelectedPages && !allGroupPagesSelected) {
        affectedGroups.push(group);
      }
    }

    if (affectedGroups.length > 0) {
      const groupNames = affectedGroups
        .map((g) => `"${g.document_type}"`)
        .join(", ");
      setError(
        `Cannot move pages: partial document group selection detected. You must move entire document groups (${groupNames}) or none of their pages.`,
      );
    } else {
      setError("");
    }
  }, [isOpen, selectedPages, documentGroups]);

  const handleConfirm = () => {
    const validationError = validateMoveTarget({
      targetPageInput: targetPage,
      totalPages,
      selectedPages,
      documentGroups,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    const target = parseInt(targetPage, 10);
    setError("");
    onConfirm(target);
    setTargetPage("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTargetPage(e.target.value);
    // validation happens automatically via useEffect
  };

  const handleClose = () => {
    setTargetPage("");
    setError("");
    onClose();
  };

  // keyboard shortcut: Enter to confirm movement
  useHotkeys(
    "enter",
    (e) => {
      if (isOpen && !isMoving && !error && targetPage) {
        e.preventDefault();
        handleConfirm();
      }
    },
    {
      enableOnFormTags: true, // allow in input fields
      preventDefault: true,
    },
    [isOpen, isMoving, error, targetPage, handleConfirm],
  );

  // keyboard shortcut: "S" to select position from modal
  // enableOnFormTags: true so it works when focus is on the number input (modal autoFocus)
  useHotkeys(
    "s",
    (e) => {
      if (isOpen && !isMoving && onSelectPosition) {
        e.preventDefault();
        onSelectPosition();
      }
    },
    {
      enabled: isOpen,
      enableOnFormTags: true,
      preventDefault: true,
    },
    [isOpen, isMoving, onSelectPosition],
  );

  // keyboard shortcut: Esc to cancel
  useHotkeys(
    "escape",
    (e) => {
      if (isOpen && !isMoving) {
        e.preventDefault();
        handleClose();
      }
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
    },
    [isOpen, isMoving, handleClose],
  );

  // early return after all hooks have been called
  if (selectedPages.size === 0) return null;

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className="flex jc-c move-pages-modal__footer">
          {onSelectPosition && (
            <Button
              className="move-pages-modal__footer-button"
              onClick={onSelectPosition}
              disabled={isMoving}
            >
              Select Position
              <span className="move-pages-modal__button-shortcut">S</span>
            </Button>
          )}
          <Button
            className="primary move-pages-modal__footer-button"
            onClick={handleConfirm}
            disabled={isMoving || !!error || !targetPage}
          >
            {isMoving ? "Moving..." : "Confirm Move"}
            <span className="move-pages-modal__button-shortcut">ENTER</span>
          </Button>
          <Button
            className="move-pages-modal__footer-button"
            onClick={handleClose}
            disabled={isMoving}
          >
            Cancel
            <span className="move-pages-modal__button-shortcut">ESC</span>
          </Button>
        </div>
      }
    >
      <Card heading="Move Pages">
        <p>
          Move <strong>{pageRange}</strong> ({pageCount} page
          {pageCount !== 1 ? "s" : ""}) to a new position in the document.
        </p>

        {error && <div className="error-message">{error}</div>}

        <div className="form-field">
          <label htmlFor="target-page">Target Position (Page Number)</label>
          <input
            id="target-page"
            type="number"
            min="1"
            max={totalPages + 1}
            value={targetPage}
            onChange={handleInputChange}
            placeholder={`Enter page number (1-${totalPages + 1})`}
            disabled={isMoving}
            autoFocus
          />
        </div>

        {targetPage && !error && (
          <div className="note-preview">
            <span className="material-icons">info</span>
            <span>
              {pageRange} will be moved to position {targetPage}
            </span>
          </div>
        )}

        <p className="help-text">
          The selected pages will be moved to start at the specified page
          number. Other pages will shift accordingly.
        </p>
      </Card>
    </KaboodleModal>
  );
};

export default MovePagesModal;
